'use strict';

/**
 * @file A Vue app that filters products in a collection based on tags
 * @author Marc Newport <marc@theworkingparty.com.au>
 * @author Lloyd Stubber <lloyd@theworkingparty.com.au>
 * @version 0.3.0
 */
(function () {

  // Get collection handle from url
  var collection = window.location.pathname.split('/').pop();

  /**
   * Initiliase Vue Router and define paths.
   */
  var router = new VueRouter({
    routes: [{ path: '/' }, { path: '/:sortBy/:tags' }]
  });

  /**
   * The product card as a component
   */
  Vue.component('product-card', {

    props: ['product'],
    template: '#product-card-template',

    data: function data() {
      return {
        productCard: null
      };
    },

    mounted: function mounted() {
      this.productCard = new theme.ProductCard(this.$el);
    },

    destroyed: function destroyed() {
      this.productCard.destroy();
    },

    computed: {

      saleText: function saleText() {
        var percentage = Math.round(this.product.price / this.product.compare_at_price * 100);
        return percentage + '% Off';
      }

    },

    methods: {

      /**
       * Get the url to the product page
       * @param  {object} product The product object
       * @return {string}
       */
      productUrl: function productUrl(product) {
        return product.handle ? '/collections/' + collection + '/products/' + product.handle : '#';
      },

      /**
       * Get the `from price` language string
       * @return {string}
       */
      fromPrice: function fromPrice() {
        return theme.strings.fromPrice;
      },

      /**
       * Get the `regular price` language string
       * @return {string}
       */
      regularPrice: function regularPrice() {
        return theme.strings.regularPrice;
      },

      /**
       * Formats a number into a money string using slate method.
       * @param  {number/string} itemPrice The price in cents (see slate docs).
       * @return {string} The formatted price.
       */
      formatMoney: function formatMoney(price) {
        return Number(price) ? slate.Currency.formatMoney(price, theme.moneyFormat) : '&nbsp;';
      },

      /**
       * Check if a product image exists or return a placeholder.
       * @param  {object} product The product object.
       * @return {string} The URL to the product image.
       */
      productImageSrc: function productImageSrc(src, size) {
        return src ? slate.Image.getSizedImageUrl(src, size) : null;
      }

    }

  });

  /**
   * The collection filter app
   */
  theme.collectionFilter = new Vue({
    el: '#collection-filter',
    router: router,
    data: {

      /*
       * Products to show per page
       */
      productsPerPage: 24,

      /*
       * Products to show per row
       */
      productsPerRow: 4,

      /*
       * Used for sorting size tags in the correct order (numbers will be displayed first)
       * Update these values to match your tags
       */
      sizes: ['XS', 'S', 'M', 'L', 'XL'],

      /*
       * The default sort by property
       */
      sortBy: 'manual',

      /**
       * Hide menu on load
       */
      showMenu: false,

      /*
       * Other properties you won't need to change
       */
      inited: false,
      products: [],
      cachedProducts: [],
      rawTags: [],
      rawTagsProcessed: false,
      tags: {},
      selectedTags: {},
      fetchedUrls: [],
      currentPage: 1,
      status: 0,
      isLoading: true,
      isFilterShowing: false
    },

    /**
     * Called when the vue instance has been created
     */
    created: function created() {

      window.addEventListener('popstate', this.setDataFromUrl.bind(this));
      this.setDataFromUrl();

      // update the grid based on the stored amount
      var storedPerRow = localStorage.getItem('productsPerRow');

      if (storedPerRow) {
        this.productsPerRow = storedPerRow;
        this.updateGrid(storedPerRow);
      }
    },

    /**
     * Called when the vue instance has been mounted
     */
    mounted: function mounted() {
      // Assign amount chosen via settings
      this.productsPerPage = Number(this.$el.dataset.productsPerPage || 24);

      if (!this.rawTags.length) {
        this.fetchProducts();
      }
    },

    // ===========================================================================
    // WATCH
    // ===========================================================================


    watch: {

      /**
       * Watches the raw tags object to update on page load - should only happen once
       */
      rawTags: function rawTags() {
        this.processRawTags();
      },

      /**
       * Watches the selected tags object to change.
       * Pushes the user back to the first page and updates the route.
       */
      selectedTags: {
        handler: function handler() {
          this.currentPage = 1;
          this.fetchProducts();
          this.updateRoute();
        },
        // Needed for nested object
        deep: true
      },

      /**
       * Watches for a change to the sort by selector
       */
      sortBy: function sortBy() {
        this.fetchProducts();
        this.updateRoute();
      },

      /**
       * If the product list has been updated, it has just finished loading
       */
      products: function products() {
        this.isLoading = false;
      }

    },

    // ===========================================================================
    // COMPUTED
    // ===========================================================================


    computed: {

      /**
       * Returns a status message based on the response state
       * @return {string}
      */
      showStatus: function showStatus() {
        switch (this.status) {
          case 500:
            return 'Internal server error, please try again.';
          case 404:
            return 'Page not found.';
          case 400:
            return 'Issue fetching data.';
          case 0:
            return 'Loading...';
        }
        return 'An unknown error occurred.';
      },

      /**
       * Returns only the products that are on the current page based on productsPerPage and after being filtered and sorted
       * @return {array} The paginated products
       */
      paginatedProducts: function paginatedProducts() {
        var maxProducts = this.productsPerPage * this.currentPage;
        return this.products.slice(0, maxProducts);
      },

      /**
       * On click, change view of products in collection filter
       */
      gridClassSize: function gridClassSize() {
        if (this.productsPerRow === 2) {
          return 'small-down--one-half medium-up--one-half';
        }

        return 'small-down--one-half medium-up--one-quarter';
      },

      /**
       * Determines if the load more button should show
       * @return {boolean}
       */
      loadingButton: function loadingButton() {
        return this.products.length >= this.currentPage * this.productsPerPage;
      }

    },

    // ===========================================================================
    // METHODS
    // ===========================================================================


    methods: {

      /**
       * Transforms the tag list into groups of facets
       */
      processRawTags: function processRawTags() {

        var self = this;

        this.rawTags.forEach(function (tag) {
          // Go to next tag if it ain't a filter group
          if (tag.indexOf('fg-') === -1) {
            return;
          }

          var split = tag.split(':');
          var group = split[0].replace('fg-', '');
          var option = split[1];

          // Check if group array exists, create it if it doesn't
          if (!self.tags[group]) {
            Vue.set(self.tags, group, []);
          }

          // Check if group array exists in the selectedTags
          if (!self.selectedTags[group]) {
            Vue.set(self.selectedTags, group, []);
          }

          // Check if tag is in array already
          if (self.tags[group].indexOf(option) === -1) {
            var otherTags = self.tags[group];
            otherTags.push(option);
            Vue.set(self.tags, group, otherTags);
          }
        });

        for (var group in this.tags) {
          this.tags[group].sort(this.sortProperly.bind(this));
        }
      },

      /**
       * Returns a list of selected tags, but in their original format eg. `fg-Color:Blue`
       * @return {array} The tags selected
       */
      getSelectedOriginalTags: function getSelectedOriginalTags() {
        var tags = [];

        for (var group in this.selectedTags) {
          var length = this.selectedTags[group].length;

          for (var i = 0; i < length; i++) {
            tags.push('fg-' + group + ':' + this.selectedTags[group][i]);
          }
        }

        return tags;
      },

      /**
       * Fetch products based on the collection, sort order, tags selected, and page
       */
      fetchProducts: function fetchProducts() {
        var _this = this;

        var page = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;


        var tags = this.getSelectedOriginalTags();
        var length = tags.length;
        var url = '/collections/' + collection + '?view=json&sort_by=' + this.sortBy + '&page=' + page;

        this.isLoading = true;

        // Are there tags selected?
        if (length) {
          for (var i = 0; i < length; i++) {
            // Escape the tags in the url
            var tag = tags[i].replace('/', '|').replace(' ', '-');
            url = '/collections/' + collection + '/' + tag + '?view=json&sort_by=' + this.sortBy + '&page=' + page;

            // Has this url been fetched already?
            if (this.fetchedUrls.indexOf(url) === -1) {
              this.isLoading = true;
              /* eslint-disable */
              $.ajax({
                url: url,
                type: 'GET',
                success: function success(response) {
                  return _this.onProductsFetched(response, url, page);
                },
                error: this.onProductsFetchedError
              });
              /* eslint-enable */
            } else {
              // The products are already in memory, so just update the sort order
              setTimeout(function () {
                _this.products = _this.sortProducts(_this.cachedProducts);
                _this.currentPage = page;
              }, 750);
            }
          }
          // Has this url been fetched already?
        } else if (this.fetchedUrls.indexOf(url) === -1) {
          $.ajax({
            url: url,
            type: 'GET',
            success: function success(response) {
              return _this.onProductsFetched(response, url, page);
            },
            error: this.onProductsFetchedError
          });
        } else {
          // The products are already in memory, so just update the sort order
          setTimeout(function () {
            _this.products = _this.sortProducts(_this.cachedProducts);
            _this.currentPage = page;
          }, 750);
        }
      },

      /**
       * The ajax success callback that merges the new requested product list into the current product list
       * @param  {string} response The ajax response
       * @param  {string} url      The url that was requested
       */
      onProductsFetched: function onProductsFetched(response, url, page) {

        this.fetchedUrls.push(url);
        this.currentPage = page;

        var products = this.inited ? this.cachedProducts : [];
        var length = products.length;
        var fetchedProducts = JSON.parse(response);

        // Only add products that aren't already in the cached list
        var uniqueProducts = fetchedProducts.filter(function (fetchedProduct) {
          for (var i = 0; i < length; i++) {
            if (products[i].id === fetchedProduct.id) {
              return false;
            }
          }
          return true;
        });

        this.cachedProducts = products.concat(uniqueProducts);
        this.products = this.sortProducts(this.cachedProducts);

        this.inited = true;
      },

      /**
       * Handle a fetch products error
       * @param  {error} error The ajax request error
       *
       * @todo handle errors
       */
      onProductsFetchedError: function onProductsFetchedError(error) {
        console.error(error);
      },

      /**
       * Fetch the next page of products
       */
      loadMoreProducts: function loadMoreProducts() {
        this.fetchProducts(this.currentPage + 1);
      },

      /**
       * Filters the products array based on the selected tags
       * @param  {array} products The product list to filter
       * @return {array}          The filtered product list
       */
      filterProducts: function filterProducts(products) {

        var self = this;

        return products.filter(function (product) {

          // Look over groups
          for (var group in self.selectedTags) {
            var length = self.selectedTags[group].length;
            // If group is empty show all
            var match = length === 0;

            // Look over tags in the group
            for (var i = 0; i < length; i++) {
              var tag = 'fg-' + group + ':' + self.selectedTags[group][i];

              if (product.tags.indexOf(tag) > -1) {
                match = true;
              }
            }
            if (!match) {
              return false;
            }
          }
          return true;
        });
      },

      /**
       * Sort the product list based on sort order chosen
       * @param  {array} products The unsorted product list to sort
       * @return {array}          The sorted product list
       */
      sortProducts: function sortProducts(products) {

        var filteredProducts = this.filterProducts(products);

        switch (this.sortBy) {

          case 'created-ascending':
            filteredProducts.sort(function (productA, productB) {
              return productA.created_at < productB.created_at ? -1 : 1;
            });
            break;

          case 'created-descending':
            filteredProducts.sort(function (productA, productB) {
              return productA.created_at > productB.created_at ? -1 : 1;
            });
            break;

          case 'title-ascending':
            filteredProducts.sort(function (productA, productB) {
              return productA.title < productB.title ? -1 : 1;
            });
            break;

          case 'title-descending':
            filteredProducts.sort(function (productA, productB) {
              return productA.title < productB.title ? 1 : -1;
            });
            break;

          case 'price-ascending':
            filteredProducts.sort(function (productA, productB) {
              return Number(productA.price) < Number(productB.price) ? -1 : 1;
            });
            break;

          case 'price-descending':
            filteredProducts.sort(function (productA, productB) {
              return Number(productA.price) < Number(productB.price) ? 1 : -1;
            });
        }

        return filteredProducts;
      },

      /**
       * Sort array numerically and alphabetically, and in size order
       * @param  {string, number} sortA sort item a
       * @param  {string, number} sortB sort item b
       * @return {number}         weight of item position
       */
      sortProperly: function sortProperly(sortA, sortB) {
        /* eslint-disable */
        if (!isNaN(Number(sortA))) {
          if (!isNaN(Number(sortB))) {
            // Both are numbers
            return Number(sortA) < Number(sortB) ? -1 : 1;
          } else {
            // sortA is a number, sortB is not
            return -1;
          }
        } else if (!isNaN(Number(sortB))) {
          // sortA is not a number, sortB is
          return 1;
        } else {
          // Are the strings sortA size (as defined in data.size)?
          var sizeA = this.sizes.indexOf(sortA);
          var sizeB = this.sizes.indexOf(sortB);

          if (sizeA > -1) {
            if (sizeB > -1) {
              return sizeA - sizeB;
            } else {
              return 1;
            }
          }

          if (sizeB > -1) {
            return -1;
          }

          // If we made it here, both are not numbers, or sizes - so just do a string compare
          return sortA < sortB ? -1 : 1;
        }
        /* eslint-enable */
      },

      /**
       * Captures the route parameters and pushes those values into the Vue data
       */
      setDataFromUrl: function setDataFromUrl() {

        // Sort by
        if (this.$route.params.sortBy) {
          this.sortBy = this.$route.params.sortBy;
        } else {
          this.sortBy = this.$route.path.split('/')[1] || this.sortBy;
        }

        // Tags
        if (this.$route.params.tags) {
          this.selectedTags = this.decodeParams(this.$route.params.tags);
        } else {
          this.clearTags();
        }
      },

      /**
       * Updates the route parameters from selected tags etc
       */
      updateRoute: function updateRoute() {
        var tags = this.selectedTags;
        var encodedTags = this.encodeParams(tags);
        router.push('/' + this.sortBy + '/' + encodedTags);
      },

      /**
       * Transfrom a JavaScript variable into a URL friendly string.
       * @param  {*} params A route parameter to be encoded.
       * @return {string} The encoded route parameter.
       */
      encodeParams: function encodeParams(params) {
        var string = '';

        for (var param in params) {
          if (params[param] && params[param].length) {
            string += param + '=' + params[param] + '&';
          }
        }

        if (string.slice(-1) === '&') {
          string = string.slice(0, -1);
        }

        return encodeURI(string.replace(' ', '+'));
      },

      /**
       * Transform an encoded variable string back into a usable variable.
       * @param  {string} url A URL parameter string.
       * @return {*} The decoded parameter.
       */
      decodeParams: function decodeParams(url) {
        var params = {};

        var string = decodeURI(url.split('+').join(' '));

        string.split('&').forEach(function (param) {
          var split = param.split('=');
          params[split[0]] = split[1].split(',');
        });

        return params;
      },

      /**
       * Gets the amount of tags selected
       * @return {number} The number of tags selected
       */
      filterCount: function filterCount() {
        var count = 0;
        for (var group in this.selectedTags) {
          count += this.selectedTags[group].length;
        }
        return count;
      },

      /**
       * Toggles the isFilterShowing boolean
       */
      toggleFilter: function toggleFilter() {
        this.isFilterShowing = !this.isFilterShowing;
      },

      /**
       * Toggles the is-expanded class name
       * @param  {object} event The click event
       */
      filterClickHandler: function filterClickHandler(event) {
        event.target.parents('.collection__filter').classList.toggle('is-expanded');
      },

      /**
       * Update the collection grid
       * @param  {number} count The amount of products per row
       */
      updateGrid: function updateGrid(count) {

        this.productsPerRow = Number(count);
        localStorage.setItem('productsPerRow', count);

        if (this.productsPerRow === 2) {
          this.productsPerPage = this.settingsPerPage;
        } else {
          this.productsPerPage = this.settingsPerPage * 4;
        }

        this.updateRoute();
      },

      /**
       * On load of filter, handleize the swatch names
       */
      swatchClass: function swatchClass(tag) {
        return 'swatch--' + tag.split(' ').join('-').toLowerCase();
      },

      /**
       * Unselect all selected tags
       */
      clearTags: function clearTags() {
        for (var group in this.selectedTags) {
          Vue.set(this.selectedTags, group, []);
        }
        this.updateRoute();
      }

    }

  });
})();